/**
 * @module app.shared
 */

import { mapIdentity } from "app/shared/lib/noop";
import { Sorting } from "app/shared/lib/essette-site";
import { localSort } from "app/shared/lib/sorting";
import { isNullOrUndefined } from 'app/shared/lib/utilities';

/**
 * Filters any records in an array which do not pass the matcher function.
 * @param {function} matcher - The matcher function.
 */
export class FilterValueConverter<T> {
    public toView(array: T[], matcher: (item: any) => boolean): T[] {
        return (array || []).filter(matcher || (_ => true));
    }
}

/**
 * Finds the first record in an array to pass the matcher function.
 * @param {function} matcher - The matcher function.
 */
export class FindValueConverter<T> {
    public toView(array: T[], matcher: (value: T) => boolean) {
        if(isNullOrUndefined(array)) { return []; }
        return array.find(matcher);
    }
}

/**
 * Filter any falsy values from an array
 */
export class ExcludeFalsyValueConverter {
    public toView(value: any[]): any[] {
        return (value || []).filter(item => !!item);
    }
}

/**
 * Flatten (cast as an array)
 */
export class FlattenValueConverter {

    public toView(value: any[]): any[] {
        if(value === null || value === undefined) { return [] };
        if(!Array.isArray(value)) { return [value] };

        return [].concat(...value);
    }
}

/**
 * Return top {count}
 */
export class TakeValueConverter {
    public toView(value: any[], count: number): any[] {
        if(value === null || value === undefined) { return [] };
        if(!Array.isArray(value)) { return [value] };

        return value.slice(0, count);
    }
}

/**
 * Splits a string delimited by {delimiter = ' '} into an array.
 * @param {string} [delimiter = ' '] - The delimiter to separate the string by.
 */
export class SplitValueConverter {
    public toView(value: string, delimiter: string = ' '): any[] {
        return (value || '').split(delimiter).filter(mapIdentity);
    }
}

/**
 * Maps an array according to a passed mapping function
 * @param {Function} mapping - Function which maps a value in the array to another value.
 */
export class MapValueConverter {
    toView(arr: any[], mapping: (val: any) => any) {
        return arr.map(mapping);
    }
}

/**
 * Locally sorts an array according to a given Sorting.
 * @param {Sorting} sorting - The processed Sorting object.
 */
export class SortValueConverter {
    toView(arr: any[], sorting: Sorting) {
        return localSort((arr || []).slice(), sorting);
    }
}

/**
 * Returns the last element of an array.
 */
export class LastValueConverter {
    toView(arr: unknown[] | unknown) {
        return Array.isArray(arr) && arr.length > 0 ? arr[arr.length - 1] : undefined;
    }
}

/**
 * Returns the first element of an array.
 */
export class FirstValueConverter {
    toView(arr: unknown[] | unknown) {
        return Array.isArray(arr) ? arr[0] : undefined;
    }
}

/**
 * Returns an array with duplicate values filtered out.
 * @param key - The optional object key to use for comparison.
 */
export class DeduplicateValueConverter {
    toView<T>(arr: T[], key?: keyof T): T[] {
        if(!Array.isArray(arr)) return arr;
        // no key means primitive values like strings, numbers
        if(!key) return Array.from(new Set(arr));
        const seen = new Set<string | number>();
        return arr.filter(item => {
            const value = item[key];
            if(seen.has(value as any)) return false;
            seen.add(value as any);
            return true;
        });
    }
}
